<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import {
  mdiPlay,
  mdiHeart,
  mdiHeartOutline,
  mdiGamepadVariant,
  mdiDiamondStone,
  mdiKeyVariant,
  mdiCheckBold,
  mdiCloseThick,
  mdiCancel,
  mdiAccount,
} from '@mdi/js'

interface Props {
  work: Work
  // ショーケース用途（ランディングページ等）では false を指定。
  // デコードのクリック操作を無効化する（バッジ表示は残す）。
  // #751: いいね♥ は interactive の対象外＝常時クリック可。
  // #752: お気に入り★ も♥同様に常時クリック可（interactive 非依存）＝未ログインは CTA。
  interactive?: boolean
  /**
   * #843: 指定するとタイトルを本物の `<a>`（NuxtLink）にする。公開ギャラリーで
   * クローラが作品ページへ辿れるようにするため。カード全体を `<a>` で包むと
   * 中の作者リンクと入れ子になってしまうので、リンクにするのはタイトルだけ。
   */
  titleHref?: string
}

const props = withDefaults(defineProps<Props>(), {
  interactive: true,
})

const emit = defineEmits(['play'])

// --- Cover (#763) ---
// 配信用 WebP を優先し、読めなかったら GIF 正本へ落ちる。作品が切り替わったら
// 判定をやり直す（前の作品の失敗を引きずらない）。
const FALLBACK_COVER = 'https://cdn.vuetifyjs.com/images/cards/forest-art.jpg'
const webpFailed = ref(false)
const coverSrc = computed(() => {
  if (!webpFailed.value && props.work.coverWebpUrl) return props.work.coverWebpUrl
  return props.work.coverImageUrl || FALLBACK_COVER
})

// --- Like ---
const { likeWork, unlikeWork } = useApi()
const { isAuthenticated } = useAuthInfo()
const { trackWorkLike } = useFunnelEvent()
const { open: openSignInCta } = useSignInCta()

// --- Decode (#609 / Phase C) ---
const decode = useDecodeAction(toRef(props, 'work'))
const { rules, user: userInfo } = storeToRefs(useGameStateStore())
const decodeMpCost = computed(() => {
  const v = rules.value?.decodeFromCrystal as { mpCost?: number } | undefined
  return v?.mpCost ?? 90
})
const isOwnWork = computed(() => {
  const author = (props.work as Work & { userId?: string }).userId
  return Boolean(author && userInfo.value?.userId && author === userInfo.value.userId)
})
// クリスタル + ステータスアイコンの 2 つ組バッジ (#611)。
// 状態:
//   - !remixable                  → ✖ (薄字、デコード不可)
//   - 自作品                      → 👤 (青、デコード不要)
//   - remixable, decoded          → ✅ (amber、参照権限あり)
//   - remixable, !decoded, !auth  → 🗝️ (サインイン誘導)
//   - remixable, !decoded, MP不足 → 🗝️ (灰色、不可)
//   - remixable, !decoded, auth   → 🗝️ (クリックでデコード)
const decodeBadge = computed(() => {
  if (!props.work.remixable) {
    return {
      statusIcon: mdiCloseThick,
      statusColor: 'grey',
      tooltip: 'デコード不可（リミックス非公開）',
      clickable: false,
    }
  }
  if (isOwnWork.value) {
    return {
      statusIcon: mdiAccount,
      statusColor: 'light-blue',
      tooltip: '自分の作品',
      clickable: false,
    }
  }
  if (decode.isAlreadyDecoded.value) {
    return {
      statusIcon: mdiCheckBold,
      statusColor: 'amber',
      tooltip: 'デコード済み',
      clickable: false,
    }
  }
  if (!isAuthenticated || !props.interactive) {
    return {
      statusIcon: mdiKeyVariant,
      statusColor: '',
      tooltip: props.interactive ? 'デコード可（サインインで利用可）' : 'デコード可（魔法研究会で利用可）',
      clickable: false,
    }
  }
  // MP 不足でもクリックは通し、ダイアログ側で MP 不足を提示する。
  return {
    statusIcon: mdiKeyVariant,
    statusColor: '',
    tooltip: `デコード（${decodeMpCost.value} MP 消費）`,
    clickable: true,
  }
})

const gamepadBadge = computed(() => {
  if (props.work.gamepadCompatible) {
    return {
      statusIcon: mdiCheckBold,
      statusColor: 'green',
      tooltip: '仮想ゲームパッド対応',
    }
  }
  return {
    statusIcon: mdiCancel,
    statusColor: 'grey',
    tooltip: '仮想ゲームパッド非対応',
  }
})

const isLiked = ref(props.work.liked ?? false)
const displayLikes = ref(props.work.likes ?? 0)
const likeLoading = ref(false)

// #805: このカードは **別の作品に使い回されうる**。`v-for` の key が作品 ID でない
// 面（トップのデスクトップグリッドが添字 key だった）はもちろん、key が作品 ID でも
// 「両方のタブに居る作品」は同じインスタンスのまま props だけ差し替わる。
// `ref(props.work.xxx)` の初期値はセットアップ時に一度しか評価されないので、
// 追従させないと**前の作品の値が残る**（本番でトップの人気タブが全カード「いいね 0」に
// なっていた ―― 初期タブ「新着」の作品がどれもいいね 0 だったため）。
//
// 作品が入れ替わったときだけ同期する（`props.work` のオブジェクト同一性ではなく
// **作品 ID** を見る）。同じ作品のまま再取得された場合は同期しないので、いいねの
// 楽観的更新（`onLikeClick`）を古いレスポンスで踏み潰す心配がない。
// #763 の表紙フォールバック判定リセットもここに同居させる。
watch(() => props.work.workId, () => {
  webpFailed.value = false
  isLiked.value = props.work.liked ?? false
  displayLikes.value = props.work.likes ?? 0
})

// Extract slug from publicUrl ("/works/{slug}")
const workSlug = computed(() => props.work.publicUrl.split('/').pop() ?? '')

// いいねツールチップ文言。:text と（role=tooltip 要素の）:aria-label で共用し、
// Lighthouse aria-tooltip-name（overlay に accessible name 必須）を満たす。#737
// #751: いいねは interactive に依らず常時ライブ（匿名可）なので、ショーケースでも
// 通常表現を出す（デコード/お気に入りは従来どおり interactive に従い抑制）。
const likeTooltip = computed(() =>
  isLiked.value ? 'いいねを取り消す' : 'いいね！',
)

const onLikeClick = async () => {
  // #751: いいねはログイン必須（#697 匿名いいねを撤回）。未ログインならサインイン CTA へ。
  // ♥ は interactive（ショーケース抑制）フラグの対象外＝どの盤面でも押せる（未ログインでも
  // クリックは通し、CTA を開く）。
  if (!isAuthenticated) {
    openSignInCta('like')
    return
  }
  if (likeLoading.value) return

  const wasLiked = isLiked.value
  // Optimistic update
  isLiked.value = !wasLiked
  displayLikes.value += wasLiked ? -1 : 1
  likeLoading.value = true

  try {
    if (wasLiked) {
      const res = await unlikeWork(workSlug.value)
      displayLikes.value = res.likes
      isLiked.value = res.liked
    }
    else {
      const res = await likeWork(workSlug.value)
      displayLikes.value = res.likes
      isLiked.value = res.liked
      trackWorkLike(workSlug.value)
    }
  }
  catch {
    // Revert on error
    isLiked.value = wasLiked
    displayLikes.value += wasLiked ? 1 : -1
  }
  finally {
    likeLoading.value = false
  }
}

// Ticker text: description + editorial comment combined
const TICKER_SEP = '\u3000'.repeat(4)
const tickerText = computed(() => {
  const desc = props.work.description || ''
  const comment = props.work.description2
  if (comment) {
    return `\u{1F4D6} ${desc}（作者）${TICKER_SEP}\u{1F989} ${comment}（Dr.D）`
  }
  return `\u{1F4D6} ${desc}（作者）`
})

// --- Overflow detection (title, description & tags) ---

const titleRef = ref<HTMLElement | null>(null)
const isTitleOverflowing = ref(false)
const descRef = ref<HTMLElement | null>(null)
const isDescOverflowing = ref(false)
const tagsRef = ref<ComponentPublicInstance | null>(null)
const isTagsOverflowing = ref(false)

let resizeObserver: ResizeObserver | null = null

const checkOverflows = () => {
  // Title
  const titleEl = titleRef.value
  if (titleEl) {
    const span = titleEl.querySelector('.work-title-text') as HTMLElement | null
    if (span) {
      isTitleOverflowing.value = span.scrollWidth > titleEl.clientWidth
    }
  }

  // Description ticker: compare first text span to wrapper width
  const descWrapper = descRef.value
  if (descWrapper) {
    const textEl = descWrapper.querySelector('.work-desc-text') as HTMLElement | null
    if (textEl) {
      isDescOverflowing.value = textEl.offsetWidth > descWrapper.clientWidth
    }
  }

  // Tags: <v-col> component → need $el to get DOM element
  const tagsEl = (tagsRef.value?.$el ?? tagsRef.value) as HTMLElement | null
  if (tagsEl) {
    const inner = tagsEl.querySelector('.work-tags-inner') as HTMLElement | null
    if (inner) {
      isTagsOverflowing.value = inner.scrollWidth > tagsEl.clientWidth
    }
  }
}

onMounted(() => {
  resizeObserver = new ResizeObserver(checkOverflows)
  if (titleRef.value) resizeObserver.observe(titleRef.value)
  if (descRef.value) resizeObserver.observe(descRef.value)
  const tagsEl = (tagsRef.value?.$el ?? tagsRef.value) as HTMLElement | null
  if (tagsEl) resizeObserver.observe(tagsEl)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <v-card
    class="ma-0 work-card"
    width="100%"
    cover
  >
    <div
      ref="descRef"
      class="work-desc-wrapper"
    >
      <span
        class="work-desc-track"
        :class="{ 'is-scrolling': isDescOverflowing }"
      >
        <span class="work-desc-text">{{ tickerText }}</span>
        <template v-if="isDescOverflowing">
          <span class="work-desc-spacer" />
          <span
            class="work-desc-text"
            aria-hidden="true"
          >{{ tickerText }}</span>
          <span class="work-desc-spacer" />
        </template>
      </span>
    </div>
    <div class="work-thumb">
      <!-- #763: 配信用 WebP を優先。`v-img` は <picture> を持てない（アスペクト比・
           プレースホルダ・フェードを自前で作り直すことになる）ので、`@error` で
           GIF 正本へ落とす。実質すべてのブラウザが animated WebP を読めるので
           発火はしない想定だが、落ちる先は用意しておく。 -->
      <v-img
        :src="coverSrc"
        @error="webpFailed = true"
      />
      <!-- お気に入り★（#724）を #752 でサムネ右上へ移設。いいね♥（メトリクス行）と
           役割・位置を明確に分離し、定石どおり目立つ位置に置く。★は♥同様に常時
           クリック可（interactive に依らず）＝未ログインはサインイン CTA。 -->
      <div class="work-thumb__fav">
        <WorkFavoriteButton :work="work" />
      </div>
    </div>
    <v-card-text class="px-2 py-1">
      <h2
        ref="titleRef"
        class="work-title-marquee"
        :class="{ 'is-overflowing': isTitleOverflowing }"
      >
        <!-- マーキーの計測は `.work-title-text` を querySelector で拾うので、
             リンクにしてもクラスさえ維持すれば壊れない（`checkOverflows`）。 -->
        <NuxtLink
          v-if="titleHref"
          class="work-title-text work-title-link"
          :to="titleHref"
        >{{ work.title || '一番面白いゲーム' }}</NuxtLink>
        <span
          v-else
          class="work-title-text"
        >{{ work.title || '一番面白いゲーム' }}</span>
      </h2>
    </v-card-text>
    <v-card-actions class="px-0 work-actions">
      <v-container
        fluid
        class="pa-0"
      >
        <v-row
          no-gutters
          class="align-end"
        >
          <v-col
            cols="4"
            class="left-pane"
          >
            <v-btn
              class="flex-fill"
              :prepend-icon="mdiPlay"
              variant="outlined"
              @click="emit('play')"
            >
              プレイ
            </v-btn>
          </v-col>

          <v-col
            cols="8"
            class="right-container"
          >
            <v-row no-gutters>
              <v-col
                ref="tagsRef"
                cols="12"
                class="right-pane work-tags"
                :class="{ 'is-overflowing': isTagsOverflowing }"
              >
                <span class="work-tags-inner">
                  <span
                    v-for="(item, i) in work.keywords || []"
                    :key="i"
                    class="work-tag-chip"
                  >{{ item }}</span>
                </span>
              </v-col>

              <v-col
                cols="12"
                class="right-pane work-metrics"
              >
                <v-icon
                  class="me-1"
                  :icon="mdiPlay"
                />
                <span>{{ work.plays || 0 }}</span>
                <span>&nbsp;</span>
                <v-tooltip
                  eager
                  :text="likeTooltip"
                  :aria-label="likeTooltip"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <!-- #751: いいねは interactive に依らず常時クリック可（匿名可）。
                         role は常に button、is-static は付けない。 -->
                    <span
                      v-bind="tooltipProps"
                      class="like-btn"
                      :class="{ 'is-liked': isLiked }"
                      role="button"
                      :aria-label="isLiked ? 'いいねを取り消す' : 'いいね'"
                      @click.stop="onLikeClick"
                    >
                      <v-icon
                        class="me-1"
                        :icon="isLiked ? mdiHeart : mdiHeartOutline"
                        :color="isLiked ? '#ef9a9a' : ''"
                      />
                    </span>
                  </template>
                </v-tooltip>
                <span class="like-count">{{ displayLikes }}</span>
                <!-- お気に入り★は #752 でサムネ右上へ移設（メトリクス行から撤去）。 -->
                <!-- Decode status badge: クリスタル + 状態アイコンの 2 つ組 (#611) -->
                <v-tooltip
                  eager
                  :text="decodeBadge.tooltip"
                  :aria-label="decodeBadge.tooltip"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <span
                      v-bind="tooltipProps"
                      class="status-badge"
                      :class="{ 'is-clickable': decodeBadge.clickable }"
                      :role="decodeBadge.clickable ? 'button' : 'img'"
                      :aria-label="decodeBadge.tooltip"
                      @click.stop="decodeBadge.clickable ? decode.open() : null"
                    >
                      <span>&nbsp;</span>
                      <v-icon :icon="mdiDiamondStone" />
                      <v-icon
                        :icon="decodeBadge.statusIcon"
                        :color="decodeBadge.statusColor"
                        size="x-small"
                        class="status-sub"
                      />
                    </span>
                  </template>
                </v-tooltip>
                <!-- Gamepad support badge: ゲームパッド + 状態アイコンの 2 つ組 -->
                <v-tooltip
                  eager
                  :text="gamepadBadge.tooltip"
                  :aria-label="gamepadBadge.tooltip"
                  location="top"
                >
                  <template #activator="{ props: tooltipProps }">
                    <span
                      v-bind="tooltipProps"
                      class="status-badge"
                      role="img"
                      :aria-label="gamepadBadge.tooltip"
                    >
                      <span>&nbsp;</span>
                      <v-icon :icon="mdiGamepadVariant" />
                      <v-icon
                        :icon="gamepadBadge.statusIcon"
                        :color="gamepadBadge.statusColor"
                        size="x-small"
                        class="status-sub"
                      />
                    </span>
                  </template>
                </v-tooltip>
              </v-col>

              <v-col
                cols="12"
                class="right-pane"
              >
                <div class="work-release-date">
                  {{ work.publishedAt }}
                </div>
                by <div class="work-provider">
                  <!-- 作者名 — authorSlug があり interactive=true のときだけ
                       /c/<slug> へのリンクにする。`@click.stop` で親 v-card の
                       潜在的なクリック伝播を抑止 (現状 play ボタンだけが
                       click を持つが、保険として)。 -->
                  <NuxtLink
                    v-if="work.authorSlug && interactive"
                    :to="`/c/${work.authorSlug}`"
                    class="author-link"
                    @click.stop
                  >
                    {{ work.author }}
                  </NuxtLink>
                  <template v-else>
                    {{ work.author }}
                  </template>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>

    <!-- Decode confirm dialog (#609 / #611) -->
    <!-- 完了通知は centering43 layout の v-snackbar-queue で表示される -->
    <DialogDecodeConfirmDialog
      v-model="decode.showConfirm.value"
      :work-title="work.title"
      :mp-cost="decodeMpCost"
      :mp-energy="userInfo?.mpEnergy ?? 0"
      :mp-capacity="userInfo?.mpCapacity ?? 0"
      @submit="decode.confirm()"
    />
  </v-card>
</template>

<style scoped>
.work-card {
  background-color: #263238f0;
  color: #ECEFF1;
  font-family: "Noto Sans JP", sans-serif;

  /* #670 Phase 3: card 自身を container にし、内部サイズを `--cu`
     (card 幅基準) に揃える。従来は `--u` (= min(1vw, 1.333vh) /
     centering43 4:3 ベース) で組んでいたため、viewport が広く card
     が 4-5 列に並ぶと「card 幅は縮むのに viewport は広い」状態で
     font/padding/button が card 幅に対して大きすぎてレイアウトが
     崩れていた。`cqi` (= container query inline-size) で card 幅
     1% を 1cqi として参照し、clamp で極端な card 幅 (小さすぎ/
     大きすぎ) でも暴れないよう floor/ceiling を効かせる。
     キャリブレーション: FHD 4 列 (card ~333px) で `--cu ≈ 13.3px`、
     旧 `--u = 14.4px` とほぼ同じレンジになる係数 4cqi を採用。

     非対応ブラウザ向けフォールバック: container queries は Safari
     16 / Chrome 105 / Firefox 110 (= baseline 2023) 以上が必要。
     旧 macOS 10.15 (Catalina) で Safari 15 止まりのユーザーが
     GA4 上で僅かに存在するため、`@supports` の外側で `--cu` を
     `--u` 経由のフォールバックに設定しておく。対応ブラウザでは
     後段の `@supports` ブロックで上書きされる。 */
  --cu: var(--u);

  font-size: max(7.22px, calc(1.02 * var(--cu)));
  z-index: 3;
}

@supports (container-type: inline-size) {
  .work-card {
    container-type: inline-size;
    --cu: clamp(8px, 4cqi, 14px);
  }
}

.work-card .v-img {
  background-color: #00000080;
  width: 100%;
  aspect-ratio: 1 / 1;
  /* width: clamp(217.22px, 30.55dvw, 391px); */
  /* height: clamp(217.22px, 30.55dvw, 391px); */
  /* max-width: 100%; */
}

/* サムネ右上のお気に入り★オーバーレイ（#752）。 */
.work-thumb {
  position: relative;
}

.work-thumb__fav {
  position: absolute;
  top: max(4px, calc(0.5 * var(--cu)));
  right: max(4px, calc(0.5 * var(--cu)));
  z-index: 4;
  display: flex;
  line-height: 0;
  /* 背景は敷かず★アイコン単体で見せる。非お気に入り時は白（currentColor）、
     任意サムネ上の視認性は drop-shadow だけで担保する。 */
  color: #fff;
  font-size: max(16px, calc(1.9 * var(--cu)));
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
}

.work-card .work-actions {
  min-height: 36px;
  padding-top: max(2.22px, calc(0.313 * var(--cu)));
  padding-bottom: max(2.22px, calc(0.313 * var(--cu)));
}

.work-desc-wrapper {
  margin: 0 4px;
  overflow: hidden;
  white-space: nowrap;
}

.work-desc-track {
  display: inline-block;
  white-space: nowrap;
}

.work-desc-text {
  white-space: nowrap;
}

.work-desc-spacer {
  display: inline-block;
  width: 6em;
}

/* Seamless loop: scroll first half (original + spacer), then snap back */
.work-desc-track.is-scrolling {
  animation: desc-scroll 30s linear infinite;
}

@keyframes desc-scroll {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}

.work-card h2 {
  font-size: max(12.2px, calc(1.72 * var(--cu)));
  font-weight: 400;
  text-align: left;
  margin: auto 0 0 -1px;
  overflow: hidden;
  white-space: nowrap;
}

.work-title-marquee {
  position: relative;
}

.work-title-text {
  display: inline-block;
  white-space: nowrap;
}

/* #843: タイトルをリンクにしても見た目は変えない（下線はホバー時だけ）。 */
.work-title-link {
  color: inherit;
  text-decoration: none;
}

.work-title-link:hover {
  text-decoration: underline;
}

.work-title-marquee.is-overflowing:hover .work-title-text {
  animation: title-scroll 4s linear infinite;
  padding-right: 3em;
}

@keyframes title-scroll {
  0% {
    transform: translateX(0%);
  }

  100% {
    transform: translateX(-100%);
  }
}

.work-card .left-pane {
  padding: 0 4px 0 8px;
  overflow: hidden;
}

.work-card .left-pane .v-btn {
  width: 100%;
  height: max(30.0px, calc(4.22 * var(--cu)));
  font-size: max(11.11px, calc(1.563 * var(--cu)));
  font-weight: bold;
  padding: 7px auto;
  padding-top: max(4.44px, calc(0.625 * var(--cu)));
  padding-bottom: max(4.44px, calc(0.625 * var(--cu)));
  margin-bottom: max(2.22px, calc(0.313 * var(--cu)));
}

.work-card .left-pane .v-btn:hover {
  background-color: #90A4AE;
}

.work-card .right-pane {
  color: #90A4AE;
  font-size: max(6.67px, calc(0.94 * var(--cu)));
  text-align: right;
  padding: 0 8px 0 4px;
  padding-right: max(4.44px, calc(0.625 * var(--cu)));
  margin: 0;
  overflow: hidden;
  text-wrap-mode: nowrap;
}

.work-card .work-tags {
  overflow: hidden;
  white-space: nowrap;
  position: relative;
}

.work-tags-inner {
  display: inline-block;
  white-space: nowrap;
}

.work-tags.is-overflowing:hover .work-tags-inner {
  animation: tags-scroll 6s linear infinite;
  padding-right: 3em;
}

@keyframes tags-scroll {
  0% {
    transform: translateX(0%);
  }

  100% {
    transform: translateX(-100%);
  }
}

.work-card .work-tag-chip {
  font-weight: 400;
  color: #ECEFF1;
  background-color: #455A64;
  padding: 0 5px 1px 5px;
  padding-bottom: max(0.5px, calc(0.078 * var(--cu)));
  margin: 0 2px 1px 2px;
  margin-bottom: max(0.5px, calc(0.156 * var(--cu)));
  border-radius: 2px;
  display: inline-block;
}

.work-card .work-metrics span {
  margin-right: 2px;
}

.like-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  opacity: 0.7;
  transition: opacity 0.15s, transform 0.1s;
}

.like-btn:hover {
  opacity: 1;
}

.like-btn.is-liked {
  opacity: 1;
}

.like-btn:active {
  transform: scale(1.25);
}

/* Decode / GamePad ステータスバッジ: アイコン + 状態アイコンの 2 つ組 (#611) */
.status-badge {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-right: 2px;
}

.status-badge .status-sub {
  margin-left: -2px;
}

.status-badge.is-clickable {
  cursor: pointer;
  transition: transform 0.1s;
}

.status-badge.is-clickable:hover {
  filter: brightness(1.2);
}

.status-badge.is-clickable:active {
  transform: scale(1.2);
}

.work-card .work-release-date {
  color: #B0BEC5;
  display: inline-block;
}

.work-card .work-provider {
  margin-right: 1px;
  color: #60e0f0;
  display: inline-block;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/* クリエイターページへのリンク (#417)。色は親 .work-provider の水色を
   継承し、ホバー時に下線で「リンク」と分かるようにする。WorkPlay.vue の
   .author-link と挙動を合わせる。 */
.work-card .author-link {
  color: inherit;
  text-decoration: none;
}

.work-card .author-link:hover {
  text-decoration: underline;
}
</style>
